import {observationVitals,fillEmptyTriageVitals} from '../lib/triageObservationVitals';
import {useDeviceDraft,hasDeviceDraft} from '../lib/useDeviceDraft';
import {offlineState} from '../lib/offlineVault';
import { ClinicalNotesPanel } from '../components/ClinicalNotesPanel';
// Triage portal — Phase 2d (nurse-driven vitals-first intake, 2026-10-03).
//
// One screen per encounter. The nurse captures vitals + chief complaint
// + acuity, then clicks "Complete triage" to send the patient on to a
// clinical department. The server handles the queue re-insert and acuity
// → priority mapping; this screen's job is just data entry.
//
// Form state follows a "controlled nullable input" pattern:
//   - '' in the textbox means "not entered" (sent as null to clear).
//   - A numeric value is parsed on blur and validated client-side so the
//     nurse sees range errors immediately rather than after a round-trip.
//
// Access control:
//   - triage.view (all clinical roles) can load this page but the inputs
//     render read-only unless the caller is nurse/admin.
//   - Server enforces write anyway via triage.write.

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    ClinicDepartment,
    ClinicMemberRole,
    TriageAcuity,
    TriageUpsertReq,
} from '@pamosi/shared';
import { TRIAGE_ACUITY_OPTIONS } from '@pamosi/shared';
import { ApiError, clinicApi, receptionApi, triageApi, encountersApi } from '../lib/api';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ACUITY_LABELS: Record<TriageAcuity, string> = {
    routine: 'Routine',
    urgent: 'Urgent',
    emergency: 'Emergency',
};

const ACUITY_DESCRIPTION: Record<TriageAcuity, string> = {
    routine: 'Stable, no red flags — send to normal queue priority.',
    urgent: 'Needs attention within the hour — jumps mid-queue.',
    emergency: 'Immediate care required — jumps to the top of the queue.',
};

function canWriteTriage(role: ClinicMemberRole | null | undefined): boolean {
    return role === 'nurse' || role === 'admin';
}

interface VitalsForm {
    bp_systolic: string;
    bp_diastolic: string;
    heart_rate: string;
    resp_rate: string;
    temp_c: string;
    spo2: string;
    weight_kg: string;
    height_cm: string;
    pain_score: string;
}

const EMPTY_VITALS: VitalsForm = {
    bp_systolic: '',
    bp_diastolic: '',
    heart_rate: '',
    resp_rate: '',
    temp_c: '',
    spo2: '',
    weight_kg: '',
    height_cm: '',
    pain_score: '',
};

export function TriageScreen() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    if (!id || !UUID_RE.test(id)) {
        return <Navigate to="/work-list" replace />;
    }

    const qc = useQueryClient();

    const { data: myClinic } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const role = myClinic?.member?.role ?? null;
    const canWrite = canWriteTriage(role);

    const encounterQ = useQuery({ queryKey: ["encounter", id], queryFn: () => encountersApi.detail(id) });

    const triageQ = useQuery({
        queryKey: ['encounter-triage', id],
        queryFn: () => triageApi.get(id),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && err.status === 404) return false;
            return failureCount < 2;
        },
    });

    // Departments for the "Complete triage" dropdown. Filter out triage
    // kinds so the nurse can't send a triaged patient *back* to triage.
    const deptsQ = useQuery({
        queryKey: ['reception-departments'],
        queryFn: () => receptionApi.listDepartments(),
        staleTime: 60_000,
    });

    const [vitals, setVitals] = useDeviceDraft<VitalsForm>('triage-vitals:'+id,EMPTY_VITALS);
    const [chiefComplaint, setChiefComplaint] = useDeviceDraft('triage-complaint:'+id,'');
    const [acuity, setAcuity] = useDeviceDraft<TriageAcuity>('triage-acuity:'+id,'routine');
    const [note, setNote] = useDeviceDraft('triage-note:'+id,'');
    const [nextDepartmentId, setNextDepartmentId] = useState<string>('');
    const [formError, setFormError] = useState<string | null>(null);
    const [savedBanner, setSavedBanner] = useState<string | null>(null);

    // Hydrate form from server triage (if present) once the fetch resolves.
    // We only hydrate on the initial load — once the user has typed, don't
    // clobber their in-flight edits with a refetch.
    const [hydrated, setHydrated] = useState(false);
    useEffect(()=>{setHydrated(false);},[id]);
    useEffect(() => {
        if (hydrated) return;
        if (triageQ.isLoading || encounterQ.isFetching) return;
        const t = triageQ.data?.triage;
        if (t&&!hasDeviceDraft('triage-vitals:'+id)) {
            setVitals({
                bp_systolic: t.bp_systolic?.toString() ?? '',
                bp_diastolic: t.bp_diastolic?.toString() ?? '',
                heart_rate: t.heart_rate?.toString() ?? '',
                resp_rate: t.resp_rate?.toString() ?? '',
                temp_c: t.temp_c?.toString() ?? '',
                spo2: t.spo2?.toString() ?? '',
                weight_kg: t.weight_kg?.toString() ?? '',
                height_cm: t.height_cm?.toString() ?? '',
                pain_score: t.pain_score?.toString() ?? '',
            });
            setChiefComplaint(t.chief_complaint ?? '');
            setAcuity(t.acuity);
            setNote(t.triage_note ?? '');
        }
        const detail=encounterQ.data;
        if(detail) setVitals(current=>fillEmptyTriageVitals(current,observationVitals(detail.observations,id,detail.encounter.patient_id)));
        setHydrated(true);
    }, [hydrated, triageQ.isLoading, triageQ.data, encounterQ.isFetching, encounterQ.data, id]);

    const carriedVitals=encounterQ.data?observationVitals(encounterQ.data.observations,id,encounterQ.data.encounter.patient_id):[];
    const triage = triageQ.data?.triage ?? null;
    const notFound = triageQ.isError
        && triageQ.error instanceof ApiError
        && triageQ.error.status === 404;
    const completed = triage?.completed_at !== null && triage?.completed_at !== undefined;

    const clinicalDepartments = useMemo<ClinicDepartment[]>(() => {
        const all = deptsQ.data?.departments ?? [];
        return all.filter((d) => d.kind !== 'triage' && d.is_active);
    }, [deptsQ.data]);

    function buildPatch(opts: { complete: boolean }): TriageUpsertReq | string {
        const parsed = parseVitals(vitals);
        if (typeof parsed === 'string') return parsed;

        const patch: TriageUpsertReq = {
            ...parsed,
            chief_complaint: chiefComplaint.trim() === '' ? null : chiefComplaint.trim(),
            acuity,
            triage_note: note.trim() === '' ? null : note.trim(),
        };
        if (opts.complete) {
            patch.complete = true;
            if (!nextDepartmentId) return 'Pick a department to send the patient to.';
            patch.next_department_id = nextDepartmentId;
        }
        return patch;
    }

    const saveMut = useMutation({
        mutationFn: (body: TriageUpsertReq) => triageApi.upsert(id, body),
        onSuccess: async (res, variables) => {
            qc.setQueryData(['encounter-triage', id], res);
            if (variables.complete) {
                // Completed + re-queued — invalidate queues so the nurse
                // sees the entry drop off, and bounce them back to the
                // queue list.
                await qc.invalidateQueries({ queryKey: ['queues'] });
                navigate('/queues');
            } else {
                setSavedBanner(offlineState()?'Saved on this device. Check connection status for server confirmation.':'Saved.');
            }
        },
    });

    function onSave() {
        setFormError(null);
        setSavedBanner(null);
        const body = buildPatch({ complete: false });
        if (typeof body === 'string') {
            setFormError(body);
            return;
        }
        saveMut.mutate(body);
    }

    function onComplete() {
        setFormError(null);
        setSavedBanner(null);
        const body = buildPatch({ complete: true });
        if (typeof body === 'string') {
            setFormError(body);
            return;
        }
        saveMut.mutate(body);
    }

    if (triageQ.isLoading || !hydrated) {
        return (
            <div className="pm-card p-8 text-center text-slate-500 text-sm">
                Loading triage…
            </div>
        );
    }

    if (triageQ.isError && !notFound) {
        const code = triageQ.error instanceof ApiError ? triageQ.error.code : null;
        const msg = code === 'forbidden_other_clinic'
            ? 'This encounter belongs to another clinic.'
            : 'Could not load triage.';
        return (
            <div className="pm-card p-6 max-w-lg">
                <div className="text-slate-800 font-medium">{msg}</div>
                <Link to={`/encounters/${id}`} className="text-brand-700 hover:underline text-sm mt-2 inline-block">
                    ← Back to encounter
                </Link>
            </div>
        );
    }

    const mutationErrorMessage = saveMut.error instanceof ApiError
        ? describeMutationError(saveMut.error)
        : saveMut.error instanceof Error
            ? saveMut.error.message
            : null;

    return (
        <div className="space-y-6 max-w-3xl">
            <div>
                <Link to={`/encounters/${id}`} className="text-brand-700 hover:underline text-sm">
                    ← Encounter
                </Link>
                <h1 className="text-slate-900 text-2xl font-semibold mt-2">Triage</h1>
                <div className="text-slate-500 text-sm mt-1">
                    {triage
                        ? completed
                            ? `Completed ${new Date(triage.completed_at!).toLocaleString()}${triage.nurse_full_name ? ` by ${triage.nurse_full_name}` : ''}`
                            : `Started ${new Date(triage.started_at).toLocaleString()}${triage.nurse_full_name ? ` by ${triage.nurse_full_name}` : ''}`
                        : 'New triage — vitals + acuity before clinical handoff.'}
                </div>
            </div>

            {!canWrite && (
                <div className="pm-card p-3 bg-slate-50 text-slate-600 text-sm">
                    Read-only — only nurses and clinic admins can edit triage.
                </div>
            )}

            {/* Vitals ---------------------------------------------------------------- */}
            <section className="pm-card p-4 space-y-3">
                <h2 className="text-slate-900 font-medium text-base">Vitals</h2>
                {carriedVitals.length>0 && <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 text-sm" role="note" aria-label="Vitals from this visit">
                    <p className="font-medium">Already recorded in this visit</p>
                    <p className="text-slate-600">Empty triage fields fill automatically from Vitals &amp; observations. Existing triage values and saved drafts stay intact. Review before saving.</p>
                    <ul className="mt-2 space-y-1">{carriedVitals.map(source=><li key={source.field}>{source.label}: <strong>{source.observation.value_numeric} {source.unit}</strong> · <time dateTime={source.observation.observed_at}>{new Date(source.observation.observed_at).toLocaleString()}</time></li>)}</ul>
                </div>}
                {encounterQ.isError && <p className="text-sm text-amber-800">Recorded observations could not be loaded. Reconnect or reopen this visit before entering the same measurements again.</p>}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <PairedBpInput
                        systolic={vitals.bp_systolic}
                        diastolic={vitals.bp_diastolic}
                        disabled={!canWrite || saveMut.isPending}
                        onChange={(sys, dia) =>
                            setVitals((v) => ({ ...v, bp_systolic: sys, bp_diastolic: dia }))
                        }
                    />
                    <NumberInput
                        label="Heart rate (bpm)"
                        value={vitals.heart_rate}
                        onChange={(x) => setVitals((v) => ({ ...v, heart_rate: x }))}
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="Resp rate (/min)"
                        value={vitals.resp_rate}
                        onChange={(x) => setVitals((v) => ({ ...v, resp_rate: x }))}
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="Temp (°C)"
                        value={vitals.temp_c}
                        onChange={(x) => setVitals((v) => ({ ...v, temp_c: x }))}
                        step="0.1"
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="SpO₂ (%)"
                        value={vitals.spo2}
                        onChange={(x) => setVitals((v) => ({ ...v, spo2: x }))}
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="Weight (kg)"
                        value={vitals.weight_kg}
                        onChange={(x) => setVitals((v) => ({ ...v, weight_kg: x }))}
                        step="0.1"
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="Height (cm)"
                        value={vitals.height_cm}
                        onChange={(x) => setVitals((v) => ({ ...v, height_cm: x }))}
                        step="0.1"
                        disabled={!canWrite || saveMut.isPending}
                    />
                    <NumberInput
                        label="Pain (0–10)"
                        value={vitals.pain_score}
                        onChange={(x) => setVitals((v) => ({ ...v, pain_score: x }))}
                        disabled={!canWrite || saveMut.isPending}
                    />
                </div>
            </section>

            {/* Chief complaint + acuity + note ---------------------------------------- */}
            <section className="pm-card p-4 space-y-4">
                <div>
                    <label className="pm-label" htmlFor="chief-complaint">Chief complaint</label>
                    <input
                        id="chief-complaint"
                        className="pm-input"
                        type="text"
                        placeholder="e.g. Fever and headache for 3 days"
                        value={chiefComplaint}
                        onChange={(e) => setChiefComplaint(e.target.value)}
                        disabled={!canWrite || saveMut.isPending}
                        maxLength={500}
                    />
                </div>

                <div>
                    <div className="pm-label">Acuity</div>
                    <div className="grid gap-2">
                        {TRIAGE_ACUITY_OPTIONS.map((opt) => (
                            <label
                                key={opt}
                                className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${
                                    acuity === opt
                                        ? 'border-brand-500 bg-brand-50'
                                        : 'border-slate-200'
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="acuity"
                                    value={opt}
                                    checked={acuity === opt}
                                    onChange={() => setAcuity(opt)}
                                    disabled={!canWrite || saveMut.isPending}
                                    className="mt-0.5"
                                />
                                <div>
                                    <div className="text-slate-900 text-sm font-medium">{ACUITY_LABELS[opt]}</div>
                                    <div className="text-slate-500 text-xs">{ACUITY_DESCRIPTION[opt]}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="pm-label" htmlFor="triage-note">Note (optional)</label>
                    <textarea
                        id="triage-note"
                        className="pm-input min-h-24"
                        placeholder="Red flags, allergies the doctor should know, context from the patient…"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        disabled={!canWrite || saveMut.isPending}
                        maxLength={2000}
                    />
                </div>
            </section>

            {encounterQ.data && <ClinicalNotesPanel patientId={encounterQ.data.encounter.patient_id} encounterId={id} section="triage" title="Triage note additions" />}

            {/* Complete + save actions ----------------------------------------------- */}
            {canWrite && (
                <section className="pm-card p-4 space-y-3">
                    <h2 className="text-slate-900 font-medium text-base">
                        {completed ? 'Re-route patient' : 'Complete triage'}
                    </h2>
                    <p className="text-slate-500 text-sm">
                        Completing stamps the triage and sends the patient to the clinical
                        department you pick below. Priority is set from the acuity above —
                        emergency jumps to the top of that queue.
                    </p>
                    <div>
                        <label className="pm-label" htmlFor="next-dept">Send to department</label>
                        <select
                            id="next-dept"
                            className="pm-input"
                            value={nextDepartmentId}
                            onChange={(e) => setNextDepartmentId(e.target.value)}
                            disabled={saveMut.isPending || deptsQ.isLoading}
                        >
                            <option value="">
                                {deptsQ.isLoading ? 'Loading departments…' : 'Pick a department…'}
                            </option>
                            {clinicalDepartments.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {formError && <div className="pm-error">{formError}</div>}
                    {mutationErrorMessage && <div className="pm-error">{mutationErrorMessage}</div>}
                    {savedBanner && (
                        <div className="text-emerald-700 text-sm">{savedBanner}</div>
                    )}

                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            className="pm-btn"
                            onClick={onSave}
                            disabled={saveMut.isPending}
                        >
                            {saveMut.isPending && !saveMut.variables?.complete ? 'Saving…' : 'Save'}
                        </button>
                        <button
                            type="button"
                            className="pm-btn-primary"
                            onClick={onComplete}
                            disabled={saveMut.isPending}
                        >
                            {saveMut.isPending && saveMut.variables?.complete
                                ? 'Completing…'
                                : completed ? 'Re-route' : 'Complete triage'}
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}

function NumberInput({
    label,
    value,
    onChange,
    disabled,
    step,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
    disabled?: boolean;
    step?: string;
}) {
    return (
        <div>
            <label className="pm-label">{label}</label>
            <input
                className="pm-input"
                type="number"
                inputMode="decimal"
                step={step ?? '1'}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
            />
        </div>
    );
}

// BP sits in a two-input group so the systolic/diastolic pair stays visually
// coupled — nurses read them off a cuff as a single reading.
function PairedBpInput({
    systolic,
    diastolic,
    onChange,
    disabled,
}: {
    systolic: string;
    diastolic: string;
    onChange: (sys: string, dia: string) => void;
    disabled?: boolean;
}) {
    return (
        <div className="sm:col-span-2">
            <label className="pm-label">Blood pressure (mmHg)</label>
            <div className="flex items-center gap-2">
                <input
                    className="pm-input"
                    type="number"
                    inputMode="numeric"
                    placeholder="Systolic"
                    value={systolic}
                    onChange={(e) => onChange(e.target.value, diastolic)}
                    disabled={disabled}
                />
                <span className="text-slate-400">/</span>
                <input
                    className="pm-input"
                    type="number"
                    inputMode="numeric"
                    placeholder="Diastolic"
                    value={diastolic}
                    onChange={(e) => onChange(systolic, e.target.value)}
                    disabled={disabled}
                />
            </div>
        </div>
    );
}

type ParsedVitals = {
    bp_systolic: number | null;
    bp_diastolic: number | null;
    heart_rate: number | null;
    resp_rate: number | null;
    temp_c: number | null;
    spo2: number | null;
    weight_kg: number | null;
    height_cm: number | null;
    pain_score: number | null;
};

interface Range {
    min: number;
    max: number;
    integer: boolean;
    label: string;
}

const RANGES: Record<keyof VitalsForm, Range> = {
    bp_systolic: { min: 40, max: 300, integer: true, label: 'Systolic BP' },
    bp_diastolic: { min: 20, max: 200, integer: true, label: 'Diastolic BP' },
    heart_rate: { min: 20, max: 300, integer: true, label: 'Heart rate' },
    resp_rate: { min: 4, max: 80, integer: true, label: 'Resp rate' },
    temp_c: { min: 25, max: 45, integer: false, label: 'Temp' },
    spo2: { min: 50, max: 100, integer: true, label: 'SpO₂' },
    weight_kg: { min: 0.3, max: 400, integer: false, label: 'Weight' },
    height_cm: { min: 20, max: 260, integer: false, label: 'Height' },
    pain_score: { min: 0, max: 10, integer: true, label: 'Pain score' },
};

// Returns the parsed numeric form, OR a string error message to surface
// at the top of the form. Mirrors the server's CHECK constraints.
function parseVitals(form: VitalsForm): ParsedVitals | string {
    const out: Partial<ParsedVitals> = {};
    for (const key of Object.keys(RANGES) as Array<keyof VitalsForm>) {
        const raw = form[key].trim();
        if (raw === '') {
            out[key] = null;
            continue;
        }
        const num = Number(raw);
        if (!Number.isFinite(num)) {
            return `${RANGES[key].label} must be a number.`;
        }
        if (RANGES[key].integer && !Number.isInteger(num)) {
            return `${RANGES[key].label} must be a whole number.`;
        }
        if (num < RANGES[key].min || num > RANGES[key].max) {
            return `${RANGES[key].label} must be between ${RANGES[key].min} and ${RANGES[key].max}.`;
        }
        out[key] = num;
    }
    // BP pair: either both set or both empty.
    if ((out.bp_systolic === null) !== (out.bp_diastolic === null)) {
        return 'Enter both systolic and diastolic BP, or leave both blank.';
    }
    return out as ParsedVitals;
}

function describeMutationError(err: ApiError): string {
    switch (err.code) {
        case 'invalid_acuity': return 'Pick a valid acuity.';
        case 'missing_next_department': return 'Pick a department before completing triage.';
        case 'invalid_next_department': return 'That department is not available.';
        case 'forbidden_other_clinic': return 'This encounter belongs to another clinic.';
        case 'insufficient_role': return 'Only nurses can edit triage.';
        case 'not_found': return 'This encounter no longer exists.';
        default: return err.message || 'Could not save triage.';
    }
}
